/**
 * Bailey's soul: how it talks to clients on the front desk, and to the owner.
 *
 * A soul is a starting point (one of four), three dials, emoji, house rules
 * and a line of anything else. Nothing here knows about React or the API.
 *
 * Until the server stores the soul itself (docs/FRONT_DESK_BACKEND.md), it is
 * written into the assistant's existing `greeting` field as short plain
 * lines the model can follow, in a shape `parseSoul` reads back. Text typed
 * on the old settings page, or by hand, comes back as a custom soul with the
 * text kept in `notes`, so nothing anyone wrote is lost.
 */

export const SOULS = [
    { id: 'warm', hue: '#ff9389', name: 'Warm and chatty', desc: 'Friendly, first names, happy to chat.', say: 3, greet: 4, energy: 2, emoji: 1, rules: ['names'] },
    { id: 'fun', hue: '#f5d46c', name: 'Fun and upbeat', desc: 'Bright, playful, a little extra energy.', say: 3, greet: 3, energy: 4, emoji: 2, rules: ['names'] },
    { id: 'polished', hue: '#91b8ff', name: 'Polished', desc: 'Calm, courteous, a touch formal.', say: 2, greet: 0, energy: 0, emoji: 0, rules: [] },
    { id: 'quick', hue: '#8bddb5', name: 'Quick and clear', desc: 'Short answers. No fuss.', say: 0, greet: 2, energy: 1, emoji: 0, rules: [] }
];
export const SOUL_IDS = SOULS.map((s) => s.id);
export const soulById = (id) => SOULS.find((s) => s.id === id) || null;

export const RULES = [
    { id: 'names', label: 'Use first names', line: 'Use first names.' },
    { id: 'addon', label: 'Suggest an add-on when it fits', line: 'Suggest a relevant add-on when it fits.' },
    { id: 'policy', label: 'Mention the cancellation policy', line: 'Mention the cancellation policy when booking.' },
    { id: 'first', label: 'Ask if it’s their first visit', line: 'Ask new clients if it is their first visit.' }
];

// Each dial has five stops. `word` is what the page shows; `line` is what
// Bailey is told.
export const DIALS = {
    say: {
        label: 'How much I say', ends: ['To the point', 'Chatty'],
        words: ['Very short', 'Short', 'Just right', 'Chatty', 'Very chatty'],
        lines: ['Keep replies to one or two short sentences.', 'Keep replies short.', 'Replies of a normal length.', 'Be chatty and add a little helpful detail.', 'Be very chatty and friendly, with helpful detail.']
    },
    greet: {
        label: 'How I greet people', ends: ['Formal', 'Warm'],
        words: ['Formal', 'Polite', 'Friendly', 'Warm', 'Very warm'],
        lines: ['Formal and courteous. No slang.', 'Polite and professional.', 'Friendly and polite.', 'Warm and friendly.', 'Very warm, like greeting a regular.']
    },
    energy: {
        label: 'My energy', ends: ['Calm', 'Fun'],
        words: ['Calm', 'Steady', 'Upbeat', 'Lively', 'Fun'],
        lines: ['Calm and reassuring.', 'Steady and calm.', 'Upbeat.', 'Lively. Light humour is fine.', 'Playful and fun, with light humour.']
    }
};
export const DIAL_IDS = ['say', 'greet', 'energy'];
export const EMOJI = {
    words: ['None', 'A few', 'Lots'],
    lines: ['No emoji.', 'At most one emoji per chat message. None on the phone.', 'A few emoji in chat. None on the phone.']
};

export const NOTES_MAX = 140;
// The API field the soul is written into holds 500 characters (docs/FRONT_DESK_BACKEND.md).
export const GREETING_MAX = 500;
const SOUL_LINE = /^Soul: (.+?)\.?$/;
const CUSTOM = 'Custom';

const clampStop = (v, fallback) => (Number.isInteger(v) && v >= 0 && v <= 4 ? v : fallback);

export const DEFAULT_SOUL = Object.freeze({ preset: 'warm', say: 3, greet: 4, energy: 2, emoji: 1, rules: ['names'], notes: '' });

/** A soul from a starting point, with the dials and rules it comes with. */
export function fromPreset(id) {
    const p = soulById(id) || soulById('warm');
    return { preset: p.id, say: p.say, greet: p.greet, energy: p.energy, emoji: p.emoji, rules: p.rules.slice(), notes: '' };
}

/** A tidy copy: stops in range, known rules only, notes tidied. */
export function normaliseSoul(input) {
    const s = input || {};
    const base = fromPreset(s.preset);
    const rules = Array.isArray(s.rules) ? RULES.map((r) => r.id).filter((id) => s.rules.includes(id)) : base.rules;
    return {
        preset: soulById(s.preset) ? s.preset : null,
        say: clampStop(s.say, base.say),
        greet: clampStop(s.greet, base.greet),
        energy: clampStop(s.energy, base.energy),
        emoji: [0, 1, 2].includes(s.emoji) ? s.emoji : base.emoji,
        rules,
        // Not cut to NOTES_MAX here: text from the old page can be longer, and
        // the soul page shows it whole until it's shortened.
        notes: String(s.notes || '').replace(/\s+/g, ' ').trim().slice(0, GREETING_MAX)
    };
}

/** True when the dials or emoji no longer match the starting point. */
export function isTuned(soul) {
    const p = soul && soulById(soul.preset);
    if (!p) return true;
    return p.say !== soul.say || p.greet !== soul.greet || p.energy !== soul.energy || p.emoji !== soul.emoji;
}

/** The preset a set of dials matches exactly, or null. */
export function presetFor(soul) {
    const hit = SOULS.find((p) => p.say === soul.say && p.greet === soul.greet && p.energy === soul.energy && p.emoji === soul.emoji);
    return hit ? hit.id : null;
}

/** What Bailey is told: the soul as short plain lines, for the greeting field. */
export function compileSoul(input) {
    const s = normaliseSoul(input);
    const p = soulById(s.preset);
    const lines = [
        `Soul: ${p ? p.name : CUSTOM}.`,
        DIALS.say.lines[s.say],
        DIALS.greet.lines[s.greet],
        DIALS.energy.lines[s.energy],
        EMOJI.lines[s.emoji]
    ];
    RULES.forEach((r) => { if (s.rules.includes(r.id)) lines.push(r.line); });
    // The field's limit is a hard stop, whatever reaches here.
    const room = GREETING_MAX - lines.join('\n').length - '\nAlso: '.length;
    const notes = s.notes.slice(0, Math.max(0, room)).trim();
    if (notes) lines.push(`Also: ${notes}`);
    return lines.join('\n');
}

/** True when the text is in the shape compileSoul writes. */
export function isSoulText(text) {
    return SOUL_LINE.test(String(text || '').split('\n')[0].trim());
}

/**
 * The soul back out of the greeting field. Text that isn't in the soul's
 * shape (the old page's free text) becomes a custom soul with the text as
 * its notes, so the person sees exactly what they wrote.
 */
export function parseSoul(text) {
    const raw = String(text || '').trim();
    if (!raw) return { ...DEFAULT_SOUL, rules: DEFAULT_SOUL.rules.slice() };
    const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!isSoulText(raw)) {
        return normaliseSoul({ preset: null, ...fromPreset('warm'), notes: raw });
    }
    const named = SOULS.find((p) => lines[0] === `Soul: ${p.name}.`);
    const stop = (dial) => {
        const at = DIALS[dial].lines.findIndex((l) => lines.includes(l));
        return at === -1 ? null : at;
    };
    const emojiAt = EMOJI.lines.findIndex((l) => lines.includes(l));
    const rules = RULES.filter((r) => lines.includes(r.line)).map((r) => r.id);
    const also = lines.find((l) => l.startsWith('Also: '));
    // Any line the reader doesn't know is kept as notes too, so a hand edit survives.
    const known = new Set([lines[0], ...DIALS.say.lines, ...DIALS.greet.lines, ...DIALS.energy.lines, ...EMOJI.lines, ...RULES.map((r) => r.line)]);
    const strays = lines.slice(1).filter((l) => !known.has(l) && !l.startsWith('Also: '));
    const notes = [also ? also.slice(6) : '', ...strays].filter(Boolean).join(' ');
    const base = named || soulById('warm');
    const soul = normaliseSoul({
        preset: named ? named.id : null,
        say: stop('say') == null ? base.say : stop('say'),
        greet: stop('greet') == null ? base.greet : stop('greet'),
        energy: stop('energy') == null ? base.energy : stop('energy'),
        emoji: emojiAt === -1 ? base.emoji : emojiAt,
        rules,
        notes
    });
    // Dials that match a starting point exactly are that starting point.
    if (!soul.preset) soul.preset = presetFor(soul);
    return soul;
}

/** One line for a row or a tile: "Warm and chatty", or "Tuned by you". */
export function soulSummary(soul) {
    const s = normaliseSoul(soul);
    const p = soulById(s.preset);
    if (p && !isTuned(s)) return p.name;
    return p ? `${p.name}, tuned` : 'Tuned by you';
}

/** The card colour for a soul, for tiles and dots. */
export function soulHue(soul) {
    const s = soul && soulById(soul.preset);
    if (s) return s.hue;
    const near = soul && presetFor(soul);
    return (near && soulById(near).hue) || SOULS[0].hue;
}

/* ------------------------------------------------------------- colours */
// The cards were designed with color-mix(), which older iOS in the app's
// shells ignores. The same mixes, worked out here and handed to the CSS as
// custom properties on each card.
const hex = (h) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(h || '').trim());
    if (!m) return [255, 147, 137];
    return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
};
const toHex = (rgb) => `#${rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
/** `pct` of colour a mixed with the rest of colour b, like color-mix(in srgb, a pct, b). */
export function mix(a, b, pct) {
    const A = hex(a); const B = hex(b); const t = pct / 100;
    return toHex(A.map((v, i) => v * t + B[i] * (1 - t)));
}
export function alpha(h, a) {
    const [r, g, b] = hex(h);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
/** Custom properties for one soul card (see frontdesk.css). */
export function hueVars(h) {
    return {
        '--hue': h,
        '--hue-85a': alpha(h, 0.85),
        '--hue-24a': alpha(h, 0.24),
        '--hue-15a': alpha(h, 0.15),
        '--hue-25a': alpha(h, 0.25),
        '--hue-35a': alpha(h, 0.35),
        '--hue-45k': alpha(mix(h, '#000000', 60), 0.45),
        '--hue-w65': mix(h, '#ffffff', 65),
        '--hue-w78': mix(h, '#ffffff', 78),
        '--hue-w85': mix(h, '#ffffff', 85),
        '--hue-deep': mix(h, '#c67050', 90),
        '--hue-navy': mix(h, '#182236', 45),
        '--hue-dusk': mix(h, '#252036', 60)
    };
}

/* -------------------------------------------------------------- samples */
/**
 * What the soul sounds like, for the preview. The same soul always says the
 * same thing. Example figures only; the real Bailey uses the salon's own.
 *
 * chatGreeting and phoneGreeting are ALSO what clients really hear when the
 * salon hasn't written its own first line: beu-node-v2 conciergeService.js
 * soulOpening is a port of these two. Change one, change both.
 */
export function chatGreeting(input, salon) {
    const s = normaliseSoul(input);
    const name = 'Bailey';
    let line;
    if (s.say <= 1) {
        line = s.greet <= 0 ? `Good afternoon. This is ${name}, the AI assistant at ${salon}. How may I help?`
            : s.greet === 1 ? `Hello. I’m ${name}, ${salon}’s AI assistant. How can I help?`
                : s.greet === 2 ? `Hi! I’m ${name}, ${salon}’s AI assistant. How can I help?`
                    : `Hiya! I’m ${name}, ${salon}’s AI assistant. What can I do for you?`;
    } else if (s.greet <= 0) {
        line = `Good afternoon. You’re speaking with ${name}, the AI assistant at ${salon}. How may I help?`;
    } else if (s.greet === 1) {
        line = `Hello. I’m ${name}, the AI assistant at ${salon}. How can I help?`;
    } else if (s.greet === 2) {
        line = `Hello! I’m ${name}, ${salon}’s AI assistant. How can I help today?`;
    } else if (s.greet === 3) {
        line = `${s.energy >= 3 ? 'Hey there!' : 'Hi there!'} I’m ${name}, ${salon}’s AI assistant. What can I do for you?`;
    } else {
        line = `Hiya! I’m ${name}, ${salon}’s AI assistant. Lovely to hear from you${s.energy >= 3 ? '!' : '.'} What can I do for you?`;
    }
    if (s.say >= 3) line = line.replace(/ (How may I help\?|How can I help( today)?\?|What can I do for you\?)$/, ' I can check prices, find you a time or pass a message to the team. $1');
    if (s.emoji === 1) line += ' 👋';
    if (s.emoji === 2) line += ' 👋✨';
    return line;
}

export function clientReply(input) {
    const s = normaliseSoul(input);
    const formal = s.greet <= 1;
    const names = s.rules.includes('names');
    const fun = s.energy >= 3;
    let open;
    if (s.greet <= 0) open = names ? 'Certainly, Sarah.' : 'Certainly.';
    else if (s.greet === 1) open = names ? 'Of course, Sarah.' : 'Of course.';
    else if (s.greet === 2) open = names ? 'Yes, Sarah, we do.' : 'Yes, we do.';
    else if (s.greet === 3) open = fun ? (names ? 'Good news, Sarah!' : 'Good news!') : (names ? 'Yes, Sarah, we do!' : 'Yes, we do!');
    else open = fun ? (names ? 'Ooh, you’re in luck, Sarah!' : 'Ooh, you’re in luck!') : (names ? 'You’re in luck, Sarah.' : 'You’re in luck.');

    let facts; let ask;
    if (s.say === 0) { facts = 'Friday, 2pm, with Sophie. £45.'; ask = formal ? 'Shall I reserve it?' : 'Shall I book it?'; }
    else if (s.say === 1) { facts = 'Sophie is free on Friday at 2pm. A cut and blow dry is £45.'; ask = formal ? 'Would you like me to reserve it?' : 'Shall I book it?'; }
    else if (s.say === 2) { facts = 'Sophie has Friday at 2pm free. A cut and blow dry is £45 and takes about an hour.'; ask = formal ? 'Would you like me to reserve it for you?' : 'Shall I book you in?'; }
    else if (s.say === 3) { facts = `Sophie has ${formal ? 'availability' : 'a gap'} on Friday at 2pm. A cut and blow dry with her is £45 and takes about an hour.`; ask = formal ? 'Would you like me to reserve it for you?' : 'Would you like me to book you in?'; }
    else { facts = `Sophie has ${formal ? 'availability' : 'a lovely gap'} on Friday at 2pm. A cut and blow dry with her is £45 and takes about an hour, so you’d be finished by 3.`; ask = formal ? 'Would you like me to reserve it? I can look at other times if that one does not suit.' : 'Shall I pop you in? If that doesn’t suit, I can find another time.'; }

    const bits = [open, facts];
    if (fun && s.say >= 2) bits.push('Fridays go fast!');
    if (s.rules.includes('addon')) bits.push(formal ? 'A gloss treatment can be added for £15, should you wish.' : fun ? 'Fancy adding a gloss for £15? It’s gorgeous.' : 'I can add a gloss treatment for £15 if you’d like.');
    if (s.rules.includes('policy')) bits.push(formal ? 'Please note that we ask for 24 hours’ notice to cancel.' : 'Just so you know, we ask for 24 hours’ notice to cancel.');
    bits.push(ask);
    if (s.rules.includes('first')) bits.push(formal ? 'May I also ask whether this is your first visit?' : 'And is it your first time with us?');
    let out = bits.join(' ');
    if (s.emoji === 1) out += ' ✂️';
    if (s.emoji === 2) out += ' ✂️✨';
    return out;
}

export function ownerReply(input) {
    const s = normaliseSoul(input);
    const formal = s.greet <= 1;
    let out;
    if (s.say === 0) out = '£4,210, up 8%. Sophie led with £1,380.';
    else if (s.say === 1) out = `${formal ? 'Takings were £4,210, up 8% on the week before.' : 'You took £4,210, up 8% on the week before.'} Sophie led with £1,380.`;
    else {
        out = `${formal ? 'Takings last week were £4,210, up 8% on the week before.' : 'You took £4,210 last week, up 8% on the week before.'} Sophie brought in the most, £1,380.`;
        if (s.say >= 3) out += ' Tuesday was your quietest day, so that’s the one to fill.';
        if (s.say >= 4) out += ' Shall I find clients who usually book midweek?';
    }
    if (s.greet >= 3) out = `${s.energy >= 3 ? 'Great week!' : 'Good week.'} ${out}`;
    if (s.emoji === 1) out += ' 📈';
    if (s.emoji === 2) out += ' 📈🎉';
    return out;
}

export function phoneGreeting(input, salon) {
    const s = normaliseSoul(input);
    const name = 'Bailey';
    if (s.say <= 1) return `${salon}, this is ${name}, the salon’s AI assistant. How can I help?`;
    if (s.greet <= 0) return `Good afternoon, ${salon}. You’re speaking with ${name}, the salon’s AI assistant. How may I help?`;
    if (s.greet === 1) return `Hello, you’re through to ${salon}. I’m ${name}, the salon’s AI assistant. How can I help?`;
    if (s.greet === 2) return `Hello, you’re through to ${salon}. I’m ${name}, the AI assistant here. How can I help today?`;
    if (s.greet === 3) return `Hi, you’re through to ${salon}! I’m ${name}, the salon’s AI assistant. What can I do for you?`;
    return `Hiya, you’re through to ${salon}! I’m ${name}, the salon’s AI assistant. Lovely to hear from you. What can I do for you?`;
}
